import { useEffect, useState } from "react";

import { BarChart, ChartFrame } from "../components/charts";
import { STATE_COLOR, sofiaStateBucket, trunkChartLabel, trunkColor } from "../components/chartTheme";
import {
  Button,
  Card,
  Check,
  EmptyState,
  Field,
  PageBody,
  PageHeader,
  Select,
  StatusPill,
  TextInput,
} from "../components/ui";
import { api, ApiError, request } from "../lib/api";
import { dash } from "../lib/format";
import { isProtectedSipId, sipDisplayName } from "../lib/sipLabels";
import { useStore } from "../lib/store";
import type { DtmfRangeResult, SipProfile, SipProfileWrite } from "../lib/types";

interface FormState {
  name: string;
  sofia_gateway_id: string;
  proxy: string;
  transport: "udp" | "tcp" | "tls";
  username: string;
  password: string;
  realm: string;
  register: boolean;
  caller_id_in_from: boolean;
  from_user: string;
  ping: string;
  expire_seconds: string;
  allowed_countries: string;
}

const EMPTY: FormState = {
  name: "",
  sofia_gateway_id: "",
  proxy: "",
  transport: "udp",
  username: "",
  password: "",
  realm: "",
  register: false,
  caller_id_in_from: true,
  from_user: "",
  ping: "25",
  expire_seconds: "600",
  allowed_countries: "",
};

const DTMF_COPY =
  "DTMF (all trunks): rfc2833 PT 101 · inband Q.23 · Goertzel 80/40/confirm 2. Never SIP INFO. This page does not originate.";

function sipTone(state: string | null | undefined): "ok" | "live" | "fail" | "mute" {
  const value = (state ?? "").toUpperCase();
  if (value === "REGED" || value === "NOREG") return "ok";
  if (value === "FAIL_WAIT") return "live";
  if (value === "UNKNOWN" || value === "OFFLINE" || !value) return "mute";
  return "fail";
}

function modeLabel(register: boolean): string {
  return register ? "Register" : "Peer / IP-bound";
}

function slugFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
}

interface HealthTrunkRow {
  trunk_id: string;
  display: string;
  sofia_state: string;
}

interface ShadowPathRow {
  country: string;
  trunk: string;
  score: number | null;
  n: number;
}

interface ShadowPath {
  rows: ShadowPathRow[];
  sample_thin: boolean;
  live_routing: boolean;
  n_min: number;
  n: number;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseHealthTrunks(raw: unknown): HealthTrunkRow[] {
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else {
    const rec = asRecord(raw);
    if (rec && Array.isArray(rec.trunks)) list = rec.trunks;
  }
  const out: HealthTrunkRow[] = [];
  for (const item of list) {
    const rec = asRecord(item);
    if (!rec) continue;
    const trunk_id = String(rec.trunk_id ?? rec.id ?? "").trim();
    if (!trunk_id) continue;
    const display = String(rec.display ?? rec.name ?? trunk_id).trim() || trunk_id;
    const sofia_state = String(rec.sofia_state ?? rec.state ?? rec.status ?? "").trim();
    out.push({ trunk_id, display, sofia_state });
  }
  return out;
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value != null && value !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function parseBestPath(raw: unknown): ShadowPath {
  const rec = asRecord(raw);
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (rec) {
    if (Array.isArray(rec.best) && rec.best.length) list = rec.best;
    else if (Array.isArray(rec.ranked)) list = rec.ranked;
  }
  const rows: ShadowPathRow[] = [];
  for (const item of list) {
    const row = asRecord(item);
    if (!row) continue;
    const country = String(row.country_iso2 ?? row.country ?? "").trim();
    const trunk = String(row.trunk_id ?? row.trunk ?? "").trim();
    if (!country && !trunk) continue;
    const score = asFiniteNumber(row.score);
    const n = asFiniteNumber(row.n) ?? 0;
    rows.push({ country: country || "—", trunk: trunk || "—", score, n });
  }
  const nMin = asFiniteNumber(rec?.n_min) ?? 30;
  const nSum = rows.reduce((acc, row) => acc + row.n, 0);
  const n = asFiniteNumber(rec?.n) ?? nSum;
  return {
    rows,
    sample_thin: rec?.sample_thin === true || n < nMin,
    live_routing: rec?.live_routing === true,
    n_min: nMin,
    n,
  };
}

function dialUsesLabel(profiles: SipProfile[], activeId: string): string {
  const fromFlag = profiles.find((profile) => profile.active);
  const fromId = profiles.find((profile) => profile.sofia_gateway_id === activeId);
  const profile = fromFlag || fromId;
  if (profile) {
    const display = sipDisplayName(profile.sofia_gateway_id, profile.name);
    return `${display} / ${profile.sofia_gateway_id}`;
  }
  if (activeId) return `${sipDisplayName(activeId)} / ${activeId}`;
  return "—";
}

function SipInsight({
  health,
  path,
}: {
  health: HealthTrunkRow[] | null;
  path: ShadowPath | null;
}) {
  const stateCounts = new Map<string, number>();
  for (const trunk of health ?? []) {
    const bucket = sofiaStateBucket(trunk.sofia_state);
    stateCounts.set(bucket, (stateCounts.get(bucket) ?? 0) + 1);
  }
  const stateLabels = [...stateCounts.keys()];
  const scoreRows =
    path && !path.sample_thin
      ? path.rows.filter((row) => row.score != null && row.n >= path.n_min).slice(0, 12)
      : [];
  if (stateLabels.length === 0 && scoreRows.length === 0) return null;
  return (
    <Card className="mb-4 px-5 py-4">
      <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Trunk insight</div>
      <p className="mt-1 text-xs text-mute">
        Shadow rank does not pick the Dial trunk. Sofia state is registration, not a route decision.
      </p>
      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        {stateLabels.length > 0 ? (
          <ChartFrame ariaLabel="SIP trunk sofia state mix" className="h-[200px]">
            <BarChart
              horizontal
              legend={false}
              labels={stateLabels}
              datasets={[
                {
                  label: "Trunks",
                  data: stateLabels.map((label) => stateCounts.get(label) ?? 0),
                  color: stateLabels.map((label) => STATE_COLOR[sofiaStateBucket(label)]),
                },
              ]}
            />
          </ChartFrame>
        ) : null}
        {scoreRows.length > 0 ? (
          <ChartFrame
            ariaLabel="Shadow score by trunk and country. Does not pick the Dial trunk."
            className="h-[200px]"
          >
            <BarChart
              horizontal
              legend={false}
              labels={scoreRows.map((row) => `${trunkChartLabel(row.trunk)} · ${row.country}`)}
              datasets={[
                {
                  label: "Shadow score",
                  data: scoreRows.map((row) => row.score ?? 0),
                  color: scoreRows.map((row) => trunkColor(row.trunk)),
                },
              ]}
            />
          </ChartFrame>
        ) : null}
      </div>
    </Card>
  );
}

function shadowLowConfidence(path: ShadowPath): boolean {
  if (path.sample_thin) return true;
  if (!path.live_routing) return true;
  if (path.n < path.n_min) return true;
  return path.rows.some((row) => row.n < path.n_min);
}

async function loadOptional<T>(
  path: string,
  signal: AbortSignal,
  parse: (raw: unknown) => T,
): Promise<T | null> {
  try {
    const body = await request<unknown>(path, { signal });
    return parse(body);
  } catch (err) {
    if (signal.aborted) return null;
    if (err instanceof ApiError && (err.status === 401 || err.status === 404)) return null;
    return null;
  }
}

/**
 * Operator SIP trunks. Never shows passwords. Codecs locked. No originate.
 */
export function Sip() {
  const { run, toast } = useStore();
  const [profiles, setProfiles] = useState<SipProfile[]>([]);
  const [active, setActive] = useState<string>("");
  const [publicIp, setPublicIp] = useState("178.83.121.210");
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dtmfNote, setDtmfNote] = useState<string>("");
  const [healthTrunks, setHealthTrunks] = useState<HealthTrunkRow[] | null>(null);
  const [bestPath, setBestPath] = useState<ShadowPath | null>(null);

  async function refresh() {
    await run(async () => {
      const data = await api.sip();
      setProfiles(data.profiles ?? []);
      setActive(data.active ?? "");
      if (data.public_ip) setPublicIp(data.public_ip);
    }, "Could not load SIP trunks");
  }

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      const [health, path] = await Promise.all([
        loadOptional("/api/orchestrator/health/trunks", ac.signal, parseHealthTrunks),
        loadOptional("/api/orchestrator/stats/best-path", ac.signal, parseBestPath),
      ]);
      if (ac.signal.aborted) return;
      setHealthTrunks(health);
      setBestPath(path);
    })();
    return () => ac.abort();
  }, []);

  const patch = (partial: Partial<FormState>) => setForm((prev) => ({ ...prev, ...partial }));

  function startAdd() {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  }

  function startEdit(profile: SipProfile) {
    setEditing(profile.sofia_gateway_id);
    setForm({
      name: profile.name,
      sofia_gateway_id: profile.sofia_gateway_id,
      proxy: profile.proxy,
      transport: profile.transport,
      username: profile.username,
      password: "",
      realm: profile.realm,
      register: profile.register,
      caller_id_in_from: profile.caller_id_in_from,
      from_user: profile.from_user,
      ping: profile.ping === null || profile.ping === undefined ? "" : String(profile.ping),
      expire_seconds: String(profile.expire_seconds ?? 600),
      allowed_countries: (profile.allowed_countries ?? []).join(", "),
    });
    setOpen(true);
  }

  function bodyFromForm(): SipProfileWrite {
    const payload: SipProfileWrite = {
      name: form.name.trim(),
      sofia_gateway_id: form.sofia_gateway_id.trim(),
      proxy: form.proxy.trim(),
      transport: form.transport,
      username: form.username.trim(),
      realm: form.realm.trim(),
      register: form.register,
      caller_id_in_from: form.caller_id_in_from,
      from_user: form.from_user.trim(),
      ping: form.ping.trim() ? Number.parseInt(form.ping, 10) : null,
      expire_seconds: Number.parseInt(form.expire_seconds, 10) || 600,
      codecs: "PCMU,PCMA",
      allowed_countries: form.allowed_countries
        .split(/[,;]/)
        .map((token) => token.trim())
        .filter(Boolean),
    };
    if (form.password) payload.password = form.password;
    return payload;
  }

  async function persist(apply: boolean) {
    const name = form.name.trim();
    const gid = form.sofia_gateway_id.trim();
    if (!name || !gid || !form.proxy.trim()) {
      toast("Name, gateway id, and proxy host:port are required", "error");
      return;
    }
    // Mirror SipProfile validators (vendor sip_profiles.py) before the API call.
    if (name.length > 40) {
      toast("Name must be 1–40 characters", "error");
      return;
    }
    if (!editing && !/^[a-z][a-z0-9_]{0,31}$/.test(gid)) {
      toast("Gateway id must match ^[a-z][a-z0-9_]{0,31}$", "error");
      return;
    }
    if (form.ping.trim()) {
      const ping = Number.parseInt(form.ping, 10);
      if (!Number.isInteger(ping) || ping < 1) {
        toast("Ping must be a whole number ≥ 1, or empty", "error");
        return;
      }
    }
    if (form.expire_seconds.trim()) {
      const expire = Number.parseInt(form.expire_seconds, 10);
      if (!Number.isInteger(expire) || expire < 1) {
        toast("Expire must be a whole number ≥ 1", "error");
        return;
      }
    }
    if (form.register && !form.username.trim()) {
      toast("Username is required when Register is on", "error");
      return;
    }
    if (form.register && !editing && !form.password) {
      toast("Password is required when Register is on", "error");
      return;
    }
    const body = bodyFromForm();
    const id = (editing || body.sofia_gateway_id || "").trim();
    setBusy(true);
    const ok = await run(async () => {
      if (editing) await api.updateSip(editing, body);
      else await api.createSip(body);
      if (apply) {
        const result = await api.applySip(id);
        const state = result.sofia_state || result.state || "";
        if (result.need_sudo) {
          toast(result.message || "Apply wrote YAML — ESL rescan needs sudo", "warn");
        } else if (state === "FAIL_WAIT" && !form.register) {
          toast("FAIL_WAIT — uncheck Register for IP-bound peers", "warn");
        } else if (result.message) {
          toast(`${result.message}${state ? ` (${state})` : ""}`, result.ok ? "ok" : "warn");
        } else {
          toast(`Applied (${state || "UNKNOWN"})`, result.ok ? "ok" : "warn");
        }
      }
    }, apply ? "Save & apply failed" : "Save SIP failed");
    setBusy(false);
    if (ok) {
      setOpen(false);
      if (!apply) toast("SIP trunk saved (yaml only)", "ok");
      await refresh();
    }
  }

  async function applyRow(id: string, register: boolean) {
    await run(async () => {
      const result = await api.applySip(id);
      const state = result.sofia_state || result.state || "";
      if (result.need_sudo) {
        toast(result.message || "Apply wrote XML — ESL rescan needs sudo", "warn");
      } else if (state === "FAIL_WAIT" && !register) {
        toast("FAIL_WAIT — uncheck Register for IP-bound peers", "warn");
      } else {
        toast(result.message || `Applied (${state || "UNKNOWN"})`, result.ok ? "ok" : "warn");
      }
    }, "Apply failed");
    await refresh();
  }

  async function testRow(id: string) {
    await run(async () => {
      const result = await api.testSip(id);
      const state = result.sofia_state || result.state || "UNKNOWN";
      toast(`Sofia ${state}`, state === "REGED" || state === "NOREG" ? "ok" : "warn");
    }, "Test failed");
    await refresh();
  }

  async function dtmfRow(id: string) {
    await run(async () => {
      const result: DtmfRangeResult = await api.dtmfRange(id);
      const state = result.sofia?.state || "UNKNOWN";
      const windowMs = result.dtmf?.window_ms ?? 80;
      const synth = result.synthetic?.ok ? "Goertzel ok" : "Goertzel fail";
      const line = `${id}: sofia ${state} · window ${windowMs}ms hop 40ms confirm 2 · ${synth}`;
      setDtmfNote(line);
      toast(line, result.ok && result.synthetic?.ok ? "ok" : "warn");
    }, "DTMF range failed");
  }

  return (
    <PageBody className="flex flex-col">
      <PageHeader
        kicker="Trunks"
        title="SIP"
        actions={
          <Button variant="primary" onClick={startAdd}>
            Add trunk
          </Button>
        }
      />
      <p className="mb-3 max-w-2xl text-sm text-mute">
        Register (digest REGED) or Peer / IP-bound trunks.{" "}
        <span className="text-ink">Save &amp; apply</span> writes yaml, renders Sofia XML, rescans
        this id only — no Coolify restart. Passwords never show. Codecs PCMU,PCMA. Contact{" "}
        {publicIp}:5060. Empty countries = all.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
          Sofia
        </span>
        {healthTrunks && healthTrunks.length > 0 ? (
          healthTrunks.map((trunk) => (
            <span key={trunk.trunk_id} className="flex items-center gap-1.5">
              <span className="text-ink-2">{trunk.display}</span>
              <StatusPill tone={sipTone(trunk.sofia_state)}>{dash(trunk.sofia_state)}</StatusPill>
            </span>
          ))
        ) : (
          <span className="text-mute">—</span>
        )}
        <span className="ml-auto text-xs text-mute">
          Dial uses {dialUsesLabel(profiles, active)}
        </span>
      </div>

      {dtmfNote ? <p className="mb-3 font-mono text-xs text-ink-2">{dtmfNote}</p> : null}

      {profiles.length === 0 ? (
        <EmptyState
          title="No SIP trunks"
          body="Add a Register trunk (user + pass) or a Peer / IP-bound trunk (register off, ping). Never invent credentials."
          action={<Button onClick={startAdd}>Add trunk</Button>}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-2xl border border-border bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-surface text-[11px] uppercase tracking-[0.16em] text-mute">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Gateway</th>
                <th className="px-4 py-3">Proxy</th>
                <th className="px-4 py-3">Mode</th>
                <th className="px-4 py-3">Countries</th>
                <th className="px-4 py-3">State</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => {
                const locked = isProtectedSipId(profile.sofia_gateway_id);
                return (
                  <tr key={profile.sofia_gateway_id} className="border-t border-teal/8">
                    <td className="px-4 py-2.5">
                      {sipDisplayName(profile.sofia_gateway_id, profile.name)}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-mute">
                      {profile.sofia_gateway_id}
                      <div className="text-[10px] uppercase tracking-wider">{profile.transport}</div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-mute">{profile.proxy}</td>
                    <td className="px-4 py-2.5 text-mute">{modeLabel(profile.register)}</td>
                    <td className="px-4 py-2.5 font-mono text-mute">
                      {(profile.allowed_countries ?? []).length
                        ? (profile.allowed_countries ?? []).join(", ")
                        : "all"}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill tone={sipTone(profile.sofia_state)}>
                        {dash(profile.sofia_state)}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-2.5 text-mute">
                      {profile.active || profile.sofia_gateway_id === active ? "active" : ""}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button variant="ghost" onClick={() => startEdit(profile)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => void applyRow(profile.sofia_gateway_id, profile.register)}
                        >
                          Apply
                        </Button>
                        <Button
                          variant="ghost"
                          title="Sofia state"
                          onClick={() => void testRow(profile.sofia_gateway_id)}
                        >
                          Test
                        </Button>
                        <Button
                          variant="ghost"
                          title="Synthetic Goertzel + sofia probe. No originate."
                          onClick={() => void dtmfRow(profile.sofia_gateway_id)}
                        >
                          DTMF
                        </Button>
                        {!profile.active && profile.sofia_gateway_id !== active ? (
                          <Button
                            variant="ghost"
                            onClick={() =>
                              void run(
                                () => api.activateSip(profile.sofia_gateway_id),
                                "Set active failed",
                              ).then((ok) => {
                                if (ok) void refresh();
                              })
                            }
                          >
                            Set active
                          </Button>
                        ) : null}
                        <Button
                          variant="danger"
                          disabled={locked}
                          title={locked ? "cd and bitcall cannot be deleted" : "Delete trunk"}
                          onClick={() => {
                            if (locked) {
                              toast("Cannot delete protected gateway cd / bitcall", "warn");
                              return;
                            }
                            void run(
                              () => api.deleteSip(profile.sofia_gateway_id),
                              "Delete failed",
                            ).then((ok) => {
                              if (ok) void refresh();
                            });
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <details className="mt-4 rounded-2xl border border-border bg-surface/60 px-4 py-3">
        <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
          Insights (shadow — does not pick the Dial trunk)
        </summary>
        <p className="mt-2 text-xs text-mute">{DTMF_COPY}</p>
        {bestPath ? (
          <div className="mt-3 overflow-auto">
            {shadowLowConfidence(bestPath) ? (
              <p className="mb-2 text-sm text-ink" role="status">
                low confidence — not Sofia state
              </p>
            ) : null}
            <table className="w-full text-left text-xs text-mute">
              <thead className="text-[10px] uppercase tracking-[0.16em]">
                <tr>
                  <th className="py-1 pr-3">Country</th>
                  <th className="py-1 pr-3">Trunk</th>
                  <th className="py-1 pr-3">Score</th>
                  <th className="py-1">n</th>
                </tr>
              </thead>
              <tbody>
                {bestPath.rows.length === 0 ? (
                  <tr>
                    <td className="py-1.5" colSpan={4}>
                      No shadow ranks yet
                    </td>
                  </tr>
                ) : (
                  bestPath.rows.map((row) => (
                    <tr key={`${row.country}:${row.trunk}`}>
                      <td className="py-1 pr-3">{row.country}</td>
                      <td className="py-1 pr-3">{row.trunk}</td>
                      <td className="py-1 pr-3 tabular-nums">
                        {row.score == null ? "—" : row.score.toFixed(3)}
                      </td>
                      <td className="py-1 tabular-nums">{row.n}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
        <SipInsight health={healthTrunks} path={bestPath} />
      </details>
      {open ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-6">
          <div className="max-h-[92vh] w-full max-w-xl overflow-auto rounded-2xl border border-border bg-navy p-6">
            <h2 className="mb-1 text-lg font-medium">{editing ? "Edit SIP trunk" : "Add SIP trunk"}</h2>
            <p className="mb-4 text-xs text-mute">
              Provider host:port, user/pass for Register, or Peer with ping (optional INVITE digest).
              Blank password on edit keeps the secret. Contact {publicIp}:5060.
            </p>
            <div className="mb-4 flex flex-wrap gap-2">
              <Button
                variant={form.register ? "primary" : "ghost"}
                onClick={() => patch({ register: true })}
              >
                Register
              </Button>
              <Button
                variant={!form.register ? "primary" : "ghost"}
                onClick={() => patch({ register: false })}
              >
                Peer / IP-bound
              </Button>
            </div>
            <p className="mb-4 text-xs text-mute">
              {form.register
                ? "Digest REGISTER. Sofia should go REGED. Username and password required."
                : "IP-bound peer (voxa / wavoip style). Leave Register off. Optional user/pass stay on INVITE. FAIL_WAIT usually means Register was left on."}
            </p>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
              Connection
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <TextInput
                  value={form.name}
                  placeholder="Provider label"
                  onChange={(event) => {
                    const name = event.target.value;
                    const next: Partial<FormState> = { name };
                    if (!editing && !form.sofia_gateway_id) {
                      const slug = slugFromName(name);
                      if (slug && /^[a-z]/.test(slug)) next.sofia_gateway_id = slug;
                    }
                    patch(next);
                  }}
                />
              </Field>
              <Field label="Gateway id" hint={editing ? "Locked on existing trunks" : "slug a–z, 0–9, _"}>
                <TextInput
                  value={form.sofia_gateway_id}
                  readOnly={Boolean(editing)}
                  placeholder="wavoip"
                  onChange={(event) => patch({ sofia_gateway_id: event.target.value })}
                />
              </Field>
              <Field label="Proxy" hint="host:port e.g. sip.wavoip.net:5060">
                <TextInput
                  value={form.proxy}
                  placeholder="sip.example.net:5060"
                  onChange={(event) => patch({ proxy: event.target.value })}
                />
              </Field>
              <Field label="Transport">
                <Select
                  value={form.transport}
                  onChange={(event) => patch({ transport: event.target.value as FormState["transport"] })}
                >
                  <option value="udp">udp</option>
                  <option value="tcp">tcp</option>
                  <option value="tls">tls</option>
                </Select>
              </Field>
            </div>

            <div className="mt-4 mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
              Auth {form.register ? "· required (Register)" : "· optional INVITE digest"}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Username" hint={form.register ? "Required" : "Optional"}>
                <TextInput
                  value={form.username}
                  autoComplete="off"
                  onChange={(event) => patch({ username: event.target.value })}
                />
              </Field>
              <Field label="Password" hint={editing ? "Blank keeps the existing secret" : undefined}>
                <TextInput
                  type="password"
                  value={form.password}
                  autoComplete="new-password"
                  onChange={(event) => patch({ password: event.target.value })}
                />
              </Field>
            </div>

            <div className="mt-4 mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
              Routing
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Allowed countries"
                hint="ISO2 or calling codes. Empty = all."
              >
                <TextInput
                  value={form.allowed_countries}
                  placeholder="BE, FR, CH"
                  onChange={(event) => patch({ allowed_countries: event.target.value })}
                />
              </Field>
              <Field label="From user">
                <TextInput
                  value={form.from_user}
                  onChange={(event) => patch({ from_user: event.target.value })}
                />
              </Field>
            </div>

            <details className="mt-4">
              <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
                Advanced
              </summary>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="Realm" hint="Defaults to proxy host">
                  <TextInput value={form.realm} onChange={(event) => patch({ realm: event.target.value })} />
                </Field>
                <Field label="Ping" hint="Seconds. Peers should ping.">
                  <TextInput value={form.ping} onChange={(event) => patch({ ping: event.target.value })} />
                </Field>
                <Field label="Expire">
                  <TextInput
                    value={form.expire_seconds}
                    onChange={(event) => patch({ expire_seconds: event.target.value })}
                  />
                </Field>
                <Field label="Codecs / contact">
                  <TextInput value={`PCMU,PCMA · ${publicIp}:5060`} readOnly />
                </Field>
              </div>
            </details>
            <div className="mt-4">
              <Check
                label="Caller ID in From"
                checked={form.caller_id_in_from}
                onChange={(caller_id_in_from) => patch({ caller_id_in_from })}
              />
            </div>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => void persist(false)}>
                Save yaml
              </Button>
              <Button variant="primary" disabled={busy} onClick={() => void persist(true)}>
                Save &amp; apply
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </PageBody>
  );
}
